import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import plymhairLogo from "@/assets/plymhair-logo.webp";
import GlossyIcon from "@/components/GlossyIcon";
import { SOCIAL_LINKS } from "@/lib/socialLinks";

const infoLinks = [
  { label: "About Us", href: "/about" },
  { label: "Visit Our Plymouth Salon", href: "/salon" },
  { label: "NHS Wig Supplier", href: "/nhs-wig-supplier" },
  { label: "Terms & Conditions", href: "/terms" },
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Delivery Info", href: "/delivery" },
  { label: "Hair Care Blog", href: "/blog" },
];

const socialLinks = [
  { name: "instagram" as const, href: SOCIAL_LINKS.instagram.href, label: "Instagram" },
  { name: "facebook" as const, href: SOCIAL_LINKS.facebook.href, label: "Facebook" },
  { name: "tiktok" as const, href: SOCIAL_LINKS.tiktok.href, label: "TikTok" },
];

const Footer = () => (
  <footer className="bg-foreground text-background/80">
    <div className="container py-16">
      <div className="shimmer-border rounded-3xl p-8 md:p-10 bg-background/[0.06] shadow-[0_0_20px_-5px_hsl(var(--primary)/0.3),0_0_60px_-15px_hsl(var(--primary)/0.15)]">
        <div className="grid md:grid-cols-4 gap-10">
          <div>
            <img src={plymhairLogo} alt="PlymHair Extensions" className="h-[6.5rem] w-auto transition-all duration-500 hover:scale-110 hover:drop-shadow-[0_0_30px_hsl(var(--primary)/0.6)] hover:brightness-110 cursor-pointer" />
            <p className="font-body text-sm leading-relaxed">
              Founded in 2015 by two sisters, Plymhair empowers women of all backgrounds with premium wigs, extensions and braiding services. NHS-approved &amp; personalised consultations available.
            </p>
          </div>
          <div>
            <h4 className="font-display font-semibold text-background mb-4">Shop</h4>
            <ul className="space-y-2 font-body text-sm">
              {["Wigs", "Extensions", "Hair Pieces", "Braids", "Head Wear", "Hair Care"].map((l) => (
                <li key={l}><Link to={`/shop?category=${l}`} className="hover:text-primary transition-colors">{l}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-display font-semibold text-background mb-4">Information</h4>
            <ul className="space-y-2 font-body text-sm">
              {infoLinks.map((l) => (
                <li key={l.label}><Link to={l.href} className="hover:text-primary transition-colors">{l.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-display font-semibold text-background mb-4">Contact Us</h4>
            <ul className="space-y-3 font-body text-sm">
              <li><a href="tel:+441752651631" className="flex items-center gap-2 hover:text-primary transition-colors"><Phone size={16} /> 01752 651631</a></li>
              <li><a href="mailto:info@plymhair.co.uk" className="flex items-center gap-2 hover:text-primary transition-colors"><Mail size={16} /> info@plymhair.co.uk</a></li>
              <li><a href="https://www.google.com/maps/search/Plymhair+Plymouth+UK" target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 hover:text-primary transition-colors"><MapPin size={16} className="mt-0.5 shrink-0" /> Plymouth, United Kingdom</a></li>
            </ul>
            <div className="flex items-center gap-3 mt-5">
              {socialLinks.map((s) => (
                <a key={s.name} href={s.href} target={s.href.startsWith("http") ? "_blank" : undefined} rel={s.href.startsWith("http") ? "noopener" : undefined} aria-label={s.label} className="hover:scale-110 transition-transform">
                  <GlossyIcon name={s.name} size={28} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="border-t border-background/10">
      <div className="container py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
        <p className="font-body text-xs text-background/50">© 2026 Plymhair & Extensions. All rights reserved.</p>
        <div className="flex items-center gap-4 font-body text-xs text-background/50">
          <Link to="/terms" className="hover:text-primary transition-colors">Terms</Link>
          <Link to="/privacy" className="hover:text-primary transition-colors">Privacy</Link>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
